import { PaymentCollectionType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/payment_collection_type_pb'
import { FormResponseItem } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/form_response_pb'
import { StoreConfig } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/store_config_pb'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormPage } from '@/components/form-template/form-page'
import {
  BookingForm,
  BookingSelection,
} from '@/lib/hooks/use-booking-selection'

type BookingSelectionFormsProps = {
  bookingSelection: BookingSelection
  storeConfig?: StoreConfig
  siteSlug: string
  source: string
  onAllFormsSubmitted?: (completedForms: CompletedForm[]) => void
  onAllFormsSubmitText?: string
  disableNotifications?: boolean
}

export type CompletedForm = {
  bookingForm: BookingForm
  formResponseId: string
}

export const BookingSelectionForms = ({
  bookingSelection,
  storeConfig,
  siteSlug,
  source,
  onAllFormsSubmitted,
  disableNotifications,
}: BookingSelectionFormsProps) => {
  const { t } = useTranslation()
  const [currentFormIndex, setCurrentFormIndex] = useState(0)
  const [completedForms, setCompletedForms] = useState<CompletedForm[]>([])

  const currentBookingForm = useMemo(() => {
    return bookingSelection.bookingForms[currentFormIndex]
  }, [bookingSelection.bookingForms, currentFormIndex])

  const nextButtonText = useMemo(() => {
    if (currentFormIndex < bookingSelection.bookingForms.length - 1) {
      return t('next')
    }

    if (bookingSelection && bookingSelection.bookingItemsPriceInCents > 0) {
      if (
        storeConfig?.paymentCollectionType ===
        PaymentCollectionType.COLLECT_UPFRONT
      ) {
        return t('proceed_to_payment')
      } else if (
        storeConfig?.paymentCollectionType ===
        PaymentCollectionType.CAPTURE_DETAILS
      ) {
        return t(t('confirm_details'))
      }
    }
    return t('schedule_event')
  }, [
    bookingSelection,
    currentFormIndex,
    storeConfig?.paymentCollectionType,
    t,
  ])

  // Handle moving to the next form
  const handleNextForm = useCallback(() => {
    if (currentFormIndex < bookingSelection.bookingForms.length - 1) {
      setCurrentFormIndex((prev) => prev + 1)
    }
  }, [bookingSelection.bookingForms.length, currentFormIndex])

  const handleFormSuccess = useCallback(
    (item: FormResponseItem) => {
      const updatedForms: CompletedForm[] = [
        ...completedForms,
        {
          bookingForm: currentBookingForm,
          formResponseId: item.id,
        },
      ]

      if (currentFormIndex < bookingSelection.bookingForms.length - 1) {
        handleNextForm()
        setCompletedForms(updatedForms)
      } else if (
        currentFormIndex ===
        bookingSelection.bookingForms.length - 1
      ) {
        onAllFormsSubmitted?.(updatedForms)
      }
    },
    [
      bookingSelection.bookingForms.length,
      completedForms,
      currentBookingForm,
      currentFormIndex,
      handleNextForm,
      onAllFormsSubmitted,
    ],
  )

  return (
    <>
      <div className="space-y-4">
        <hr />

        <div className="mb-4 flex flex-row items-center justify-between text-muted-foreground">
          <div className="font-semibold text-primary">
            {currentBookingForm.summary}
          </div>
          {bookingSelection.bookingForms.length > 1 && (
            <div>
              {t('form')} {currentFormIndex + 1} of{' '}
              {bookingSelection.bookingForms.length}
            </div>
          )}
        </div>

        <FormPage
          key={currentBookingForm.formTemplateId}
          formTemplateId={currentBookingForm.formTemplateId}
          siteSlug={siteSlug}
          source={source}
          disableNotifications={disableNotifications}
          onSuccess={handleFormSuccess}
          overrideButtonText={nextButtonText}
        />
      </div>
    </>
  )
}
